/**
 * Tile Canvas and Tile (piece 4): the responsive grid under every Tile grid.
 * One block equals one column width and serves both dimensions; a Tile spans
 * 1, 2 or 3 columns and is a whole number of blocks tall; order survives every
 * restack; three columns is a ceiling, not a target; a Tile's own minWidth can
 * force the canvas to drop a column, and the canvas never reaches inside a
 * Tile. Restacking happens in explicit edit mode only.
 */
export type TileSpan = 1 | 2 | 3;

export interface TileBounds {
  minWidth: number;
  maxWidth: number;
}

export interface TilePlacement {
  id: string;
  span: TileSpan;
  /** Height in blocks, a whole number. */
  height: number;
  locked: boolean;
}

export const MAX_TILE_COLUMNS = 3;

export function assertBlockHeight(height: number): number {
  if (!Number.isInteger(height) || height < 1) throw new Error('A Tile height is a whole number of blocks, never a pixel height (piece 4)');
  return height;
}

export interface CanvasOptions {
  /** The declared column count, 3 by default. */
  columns?: number;
  /** Widths below which the canvas drops to 2 and then to 1 column; declared against the breakpoint scale. */
  twoColumnBelow: number;
  oneColumnBelow: number;
  gap?: number;
  /**
   * The block's height as a fraction of the column width (decision 0263). One
   * block is one column width by default; the record pages and the dashboard
   * set the fraction the source figures draw, so a one-block tile is a card
   * and not a square.
   */
  blockHeightRatio?: number;
}

export const DEFAULT_BLOCK_HEIGHT_RATIO = 1;

/**
 * The gutter between two Tiles, in pixels, and the value the `--tile-gap`
 * token carries (decision 0601). 🔨 The canvas needs the number to divide its
 * own width and the stylesheet needs the token to draw the gap, so the two are
 * declared once here and held together by the token suite. 💰 The dashboard
 * passed a raw 12 of its own, which no check could see because the pixel was
 * assembled inside a template string (product owner, 2026-09-20).
 */
export const TILE_GAP_PX = 12;

/** The figures' own proportion: a one-block tile about six tenths as tall as it is wide (decision 0263). */
export const FIGURE_BLOCK_HEIGHT_RATIO = 0.6;

/** A block's height in pixels for a column of the given width; the unit stays the column, scaled once per canvas. */
export function blockHeightFor(columnWidth: number, options: Pick<CanvasOptions, 'blockHeightRatio'>): number {
  const ratio = options.blockHeightRatio ?? DEFAULT_BLOCK_HEIGHT_RATIO;
  if (!(ratio > 0) || ratio > 1) throw new Error('A block is between a sliver and a full column width tall, as a fraction of the column (decision 0263)');
  return Math.max(1, Math.floor(columnWidth * ratio));
}

/** Column count: the declared ceiling, dropped by width, by tile count, and by any Tile whose minWidth the column cannot meet. */
/** The canvas's row unit at two columns and three (decision 0321): a Tile's measured height rounds up to whole units. */
export const TILE_ROW_UNIT = 8;

/** How many unit rows a Tile of the given pixel height spans, the row gap counted, never fewer than one. */
export function tileRowsFor(pixelHeight: number, unit: number = TILE_ROW_UNIT, gap = 0): number {
  if (!(pixelHeight > 0)) return 1;
  return Math.max(1, Math.ceil((pixelHeight + gap) / (unit + gap)));
}

/** The most a Tile may stand, in pixels: its declared blocks plus the gaps between them. */
export function maxHeightFor(blocks: number, blockHeight: number, gap: number): number {
  return blocks * blockHeight + (blocks - 1) * gap;
}

export function columnsFor(canvasWidth: number, tiles: readonly { bounds: TileBounds }[], options: CanvasOptions): number {
  const ceiling = Math.min(options.columns ?? MAX_TILE_COLUMNS, MAX_TILE_COLUMNS);
  const gap = options.gap ?? 0;
  let columns = ceiling;
  if (canvasWidth < options.oneColumnBelow) columns = 1;
  else if (canvasWidth < options.twoColumnBelow) columns = Math.min(columns, 2);
  columns = Math.max(1, Math.min(columns, tiles.length || 1));
  const widest = tiles.reduce((m, t) => Math.max(m, t.bounds.minWidth), 0);
  while (columns > 1 && (canvasWidth - gap * (columns - 1)) / columns < widest) columns -= 1;
  return columns;
}

export interface PlacedTile {
  id: string;
  column: number;
  row: number;
  span: number;
  /** The rows the Tile's content needs, measured or declared. */
  height: number;
  /**
   * The rows the Tile stands in: its height, stretched down to where the next Tile in any of its columns
   * starts, or to the canvas's foot (decision 1080). Never less than its height.
   */
  stand: number;
}

/**
 * 🔒 At two columns and three each column is its own stack (decision 1080; product owner, 2026-09-27):
 * each Tile, in the locked order, takes the lowest place its span fits, the leftmost on a tie, directly
 * under what already stands in those columns. No row runs across the columns, so a short Tile beside a
 * tall one leaves no void. At two columns each Tile starts no higher than the one before it, so the page
 * reads in the locked order top to bottom.
 * 🔒 A Tile then stretches down to where the next Tile in any of its columns starts, or to the canvas's
 * foot, keeping the rows its content needs and never fewer (`stand`). A Tile named in `slim`, an empty
 * record-page tile drawn as its title row alone, stands at its own height (decision 6700).
 * 🔨 A Tile also widens over a cell beside it that the finished grid leaves empty on every row it stands
 * on, to its right first and then to its left (product owner, 2026-09-11): a last Tile with an empty
 * column beside it takes that column.
 * 🔨 At one column a Tile is one row tall and the row is its content's height (decision 0287): a block is a
 * column wide, so a two-block Tile on a phone was two screen widths tall over four rows of text.
 * 🔨 At two columns and three a Tile's content height is measured in row units (decision 0321). `rows`
 * carries each measured height in units; a Tile with no measurement yet is laid out at its declared
 * blocks, so the first paint has a shape.
 * 💰 The canvas laid its Tiles out in rows with a cursor until 2026-09-27, so a short Tile beside a tall
 * one left a void the height of the difference, and the voids stacked down the Company page.
 */
export function layoutTiles(tiles: readonly TilePlacement[], columns: number, rows?: Readonly<Record<string, number>>, slim: ReadonlySet<string> = NO_SLIM_TILES): PlacedTile[] {
  const spanOf = (tile: TilePlacement) => (columns === 1 ? 1 : Math.min(tile.span, columns));
  const heightOf = (tile: TilePlacement) => (columns === 1 ? 1 : rows?.[tile.id] ?? tile.height);
  const bottoms = new Array<number>(columns).fill(0);
  const placed: PlacedTile[] = tiles.map((tile) => {
    assertBlockHeight(tile.height);
    const span = spanOf(tile);
    const height = heightOf(tile);
    let column = 0;
    let row = Number.POSITIVE_INFINITY;
    for (let c = 0; c + span <= columns; c += 1) {
      const top = Math.max(...bottoms.slice(c, c + span));
      if (top < row) {
        row = top;
        column = c;
      }
    }
    for (let c = column; c < column + span; c += 1) bottoms[c] = row + height;
    return { id: tile.id, column, row, span, height, stand: height };
  });
  return columns === 1 ? placed : stretchDown(widenOverHoles(placed, columns), slim);
}

const NO_SLIM_TILES: ReadonlySet<string> = new Set();

/**
 * Each Tile's stand: down to the nearest start of a Tile below it in any column it covers, or to the
 * canvas's foot where nothing is below it (decision 1080). The Tile keeps its own height as the least.
 * 🔒 An empty record-page tile is one slim row and takes no stretch (decision 6700, amending 1080): the
 * room under it stays the ground, and it stands at its own height until a row arrives.
 */
function stretchDown(placed: PlacedTile[], slim: ReadonlySet<string>): PlacedTile[] {
  const foot = placed.reduce((m, p) => Math.max(m, p.row + p.height), 0);
  return placed.map((p) => {
    if (slim.has(p.id)) return { ...p, stand: p.height };
    let boundary = foot;
    for (const q of placed) {
      const shares = q.column < p.column + p.span && p.column < q.column + q.span;
      if (q !== p && shares && q.row >= p.row + p.height) boundary = Math.min(boundary, q.row);
    }
    return { ...p, stand: Math.max(p.height, boundary - p.row) };
  });
}

/**
 * The finished grid's holes, filled by the Tile beside each: rightwards first, then leftwards, until
 * none is left that a neighbour can take sideways. 💰 A downward growth was added on 2026-09-11 and
 * retired by decision 0321; decision 1080 brings a Tile's stretch back as `stand`, down to the next
 * Tile in its own column only, never over a well a row alignment made, because no row alignment is left.
 */
function widenOverHoles(placed: PlacedTile[], columns: number): PlacedTile[] {
  const rows = placed.reduce((m, p) => Math.max(m, p.row + p.height), 0);
  const cells = Array.from({ length: rows }, () => new Array<string | null>(columns).fill(null));
  const fill = (p: PlacedTile) => {
    for (let r = p.row; r < p.row + p.height; r += 1) for (let c = p.column; c < p.column + p.span; c += 1) cells[r]![c] = p.id;
  };
  placed.forEach(fill);
  const emptyColumn = (p: PlacedTile, column: number) => {
    if (column < 0 || column >= columns) return false;
    for (let r = p.row; r < p.row + p.height; r += 1) if (cells[r]![column] !== null) return false;
    return true;
  };
  let changed = true;
  while (changed) {
    changed = false;
    for (const p of placed) {
      while (emptyColumn(p, p.column + p.span)) {
        p.span += 1;
        fill(p);
        changed = true;
      }
    }
    for (const p of placed) {
      while (emptyColumn(p, p.column - 1)) {
        p.column -= 1;
        p.span += 1;
        fill(p);
        changed = true;
      }
    }
  }
  return placed;
}

/** The order is the list order at every column count. */
export function orderOf(placed: readonly PlacedTile[]): string[] {
  return placed.map((p) => p.id);
}

export interface TileEditState {
  editing: boolean;
  tiles: TilePlacement[];
}

export type TileEditEvent =
  | { type: 'enterEdit' }
  | { type: 'exitEdit' }
  | { type: 'move'; id: string; toIndex: number }
  | { type: 'resize'; id: string; span?: TileSpan; height?: number }
  | { type: 'remove'; id: string }
  | { type: 'viewportChanged' };

/** Locked Tiles never move, resize or go; nothing changes outside edit mode; a viewport change reorders nothing. */
export function tileEditReducer(state: TileEditState, event: TileEditEvent): TileEditState {
  switch (event.type) {
    case 'enterEdit':
      return { ...state, editing: true };
    case 'exitEdit':
      return { ...state, editing: false };
    case 'viewportChanged':
      return state;
    case 'move': {
      if (!state.editing) return state;
      const from = state.tiles.findIndex((t) => t.id === event.id);
      if (from < 0 || state.tiles[from]!.locked) return state;
      const tiles = [...state.tiles];
      const [tile] = tiles.splice(from, 1);
      tiles.splice(Math.max(0, Math.min(event.toIndex, tiles.length)), 0, tile!);
      return { ...state, tiles };
    }
    case 'resize': {
      if (!state.editing) return state;
      return {
        ...state,
        tiles: state.tiles.map((t) => {
          if (t.id !== event.id || t.locked) return t;
          const height = event.height === undefined ? t.height : assertBlockHeight(event.height);
          return { ...t, span: event.span ?? t.span, height };
        }),
      };
    }
    case 'remove': {
      if (!state.editing) return state;
      return { ...state, tiles: state.tiles.filter((t) => t.id !== event.id || t.locked) };
    }
  }
}

/** The per-user arrangement store: SavedView once Application shell declares it; phase 1 holds it in memory behind the same interface. */
export interface ArrangementStore {
  load(scopeKey: string): Promise<TilePlacement[] | null>;
  save(scopeKey: string, tiles: readonly TilePlacement[]): Promise<void>;
}

export function inMemoryArrangementStore(): ArrangementStore {
  const rows = new Map<string, TilePlacement[]>();
  return {
    load: async (scopeKey) => rows.get(scopeKey)?.map((t) => ({ ...t })) ?? null,
    save: async (scopeKey, tiles) => {
      rows.set(scopeKey, tiles.map((t) => ({ ...t })));
    },
  };
}

/**
 * The Tile's item-type renderers. `linkRow` is the tenth, added with the
 * dashboard: a work-queue count links out to where the queue already lives
 * (decision 0116), and none of the other nine carries a route. `contactRow` is
 * the eleventh, and it is the standard every phone and email item draws through
 * (decision 0623).
 */
export const tileItemTypes = ['statusOnly', 'labelValueGrid', 'note', 'person', 'file', 'location', 'fullWidthRow', 'timeline', 'checklist', 'linkRow', 'contactRow'] as const;
export type TileItemType = (typeof tileItemTypes)[number];
